import React, { useState, useMemo } from 'react';
import Icon from './Icon';
import SearchableSelect from './ui/SearchableSelect';
import { FloorPlan, Room } from '../types/Planner';
import { GuestGroup, Guest } from '../types/ui/Guests';
import TableShapeGlyph, { shapeName } from './floor-plan-editor/TableShapeGlyph';
import { sectionLetterForRoom } from './floor-plan-editor/sectionGeometry';
import { tableLabel } from './floor-plan-editor/tableGeometry';

interface SharedFloorPlanViewProps {
    floorPlan: FloorPlan;
    venueName: string;
    guestGroups: GuestGroup[];
    guests: Guest[];
    onClose: () => void;
}

function getStatusColor(filled: number, total: number): string {
    if (filled === 0) return '#94a3b8';
    if (filled >= total) return '#22c55e';
    return '#f59e0b';
}

export const SharedFloorPlanView: React.FC<SharedFloorPlanViewProps> = ({
    floorPlan,
    venueName,
    guestGroups,
    guests,
    onClose,
}) => {
    const [filterGroupId, setFilterGroupId] = useState('');

    const tables = useMemo(
        () => floorPlan.rooms.filter(r => r.type === 'table'),
        [floorPlan.rooms]
    );

    const zones = useMemo(
        () => floorPlan.rooms.filter(r => r.type !== 'table'),
        [floorPlan.rooms]
    );

    const groupMap = useMemo(
        () => new Map(guestGroups.map(g => [g.id, g])),
        [guestGroups]
    );

    const guestCountByGroup = useMemo(() => {
        const map: Record<string, number> = {};
        for (const g of guestGroups) {
            map[g.id] = g.guestIds?.length ?? guests.filter(gu => gu.group === g.name).length;
        }
        return map;
    }, [guestGroups, guests]);

    const filteredTables = useMemo(
        () => filterGroupId ? tables.filter(t => t.groupId === filterGroupId) : tables,
        [tables, filterGroupId]
    );

    // Sort by position: top-left order
    const sortedTables = useMemo(
        () => [...filteredTables].sort((a, b) =>
            (a.position.y + a.position.x) - (b.position.y + b.position.x)
        ),
        [filteredTables]
    );

    // Groups that have at least one table
    const activeGroups = useMemo(
        () => guestGroups.filter(g => tables.some(t => t.groupId === g.id)),
        [guestGroups, tables]
    );

    const getTableFill = (room: Room): { filled: number; total: number } => {
        const total = room.seats ?? room.capacity ?? 0;
        if (room.namedSeats && room.namedSeats.length > 0) {
            const filled = room.namedSeats.filter(s => s.guestName.trim()).length;
            return { filled, total };
        }
        if (room.groupId) {
            const grp = groupMap.get(room.groupId);
            if (grp) {
                const groupGuests = guestCountByGroup[room.groupId] ?? 0;
                const tablesInGroup = tables.filter(t => t.groupId === room.groupId).length;
                const approxFilled = tablesInGroup > 0 ? Math.round(groupGuests / tablesInGroup) : 0;
                return { filled: Math.min(approxFilled, total), total };
            }
        }
        return { filled: 0, total };
    };

    return (
        <div className="vm-mobile-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="vm-mobile-panel">
                {/* Header */}
                <div className="vm-mobile-header">
                    <div className="vm-mobile-header-info">
                        <span className="vm-mobile-shared-badge">Shared link · read-only</span>
                        <span className="vm-mobile-venue">{venueName}</span>
                        <span className="vm-mobile-fp-name">{floorPlan.name}</span>
                    </div>
                    <button className="vm-mobile-close" onClick={onClose} aria-label="Close"><Icon name="close" size={16} /></button>
                </div>

                {/* Background photo */}
                {(floorPlan.imageUrl || floorPlan.imageData) && (
                    <div className="vm-mobile-photo">
                        <img
                            src={floorPlan.imageUrl ?? floorPlan.imageData}
                            alt="Venue floor plan"
                        />
                    </div>
                )}

                {/* Stats strip */}
                <div className="vm-mobile-stats">
                    <div className="vm-mobile-stat">
                        <strong>{tables.length}</strong>
                        <span>Tables</span>
                    </div>
                    <div className="vm-mobile-stat">
                        <strong>{tables.reduce((s, t) => s + (t.seats ?? t.capacity ?? 0), 0)}</strong>
                        <span>Seats</span>
                    </div>
                    <div className="vm-mobile-stat">
                        <strong>{zones.length}</strong>
                        <span>Zones</span>
                    </div>
                    <div className="vm-mobile-stat">
                        <strong>{activeGroups.length}</strong>
                        <span>Groups</span>
                    </div>
                </div>

                {/* Filter */}
                {activeGroups.length > 0 && (
                    <div className="vm-mobile-filter">
                        <SearchableSelect
                            className="form-input form-input--sm"
                            value={filterGroupId}
                            onChange={setFilterGroupId}
                            options={[
                                { value: '', label: 'All groups' },
                                ...activeGroups.map(g => ({ value: g.id, label: g.name })),
                            ]}
                        />
                        {filterGroupId && (
                            <button className="vm-mobile-clear-filter" onClick={() => setFilterGroupId('')}>
                                Clear
                            </button>
                        )}
                    </div>
                )}

                {/* Table grid */}
                <div className="vm-mobile-grid">
                    {sortedTables.length === 0 ? (
                        <p className="vm-mobile-empty">No tables on this floor plan yet.</p>
                    ) : sortedTables.map(room => {
                        const group = room.groupId ? groupMap.get(room.groupId) : undefined;
                        const { filled, total } = getTableFill(room);
                        const letter = sectionLetterForRoom(room, floorPlan.rooms);
                        const statusColor = total > 0 ? getStatusColor(filled, total) : '#94a3b8';

                        return (
                            <div key={room.id} className="vm-mobile-table-card">
                                {group && (
                                    <div
                                        className="vm-mobile-table-group-bar"
                                        style={{ /* ds-dynamic: table group color from data */ background: group.color ?? '#9ca3af' }}
                                    />
                                )}
                                <div className="vm-mobile-table-body">
                                    <div className="vm-mobile-table-top">
                                        <span className="vm-mobile-table-num">{tableLabel(room.name, letter)}</span>
                                        <span
                                            className="vm-mobile-table-dot"
                                            style={{ /* ds-dynamic: table fill status color computed from data */ background: statusColor }}
                                            title={filled === 0 ? 'Empty' : filled >= total ? 'Full' : 'Partial'}
                                        />
                                    </div>
                                    <div className="vm-mobile-table-shape">
                                        <TableShapeGlyph shape={room.tableShape} />
                                        <span>{shapeName(room.tableShape)}{letter ? `, section ${letter}` : ''}</span>
                                    </div>
                                    {room.isHeadTable && (
                                        <div className="vm-mobile-vvip">★ VVIP</div>
                                    )}
                                    {group && (
                                        <div className="vm-mobile-table-group">{group.name}</div>
                                    )}
                                    <div className="vm-mobile-table-seats">
                                        {total > 0 ? `${filled}/${total} seats` : `${room.seats ?? room.capacity ?? '?'} seats`}
                                    </div>
                                    {room.assignedPurpose && (
                                        <div className="vm-mobile-table-purpose">{room.assignedPurpose}</div>
                                    )}
                                    {room.namedSeats && room.namedSeats.length > 0 && (
                                        <div className="vm-mobile-named-seats">
                                            {room.namedSeats.map(s => (
                                                <div key={s.seatIndex} className="vm-mobile-named-seat">
                                                    <span className="vm-mobile-seat-num">#{s.seatIndex + 1}</span>
                                                    <span className="vm-mobile-seat-name">
                                                        {s.guestName || <em className="vm-mobile-seat-empty">empty</em>}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Zone list */}
                {zones.length > 0 && !filterGroupId && (
                    <div className="vm-mobile-zones">
                        <div className="vm-mobile-section-title">Zones</div>
                        {zones.map(z => {
                            const tablesInZone = tables.filter(t => t.section === z.name);
                            return (
                                <div key={z.id} className="vm-mobile-zone-row">
                                    <span className="vm-mobile-zone-name">{z.name}</span>
                                    {z.capacity && (
                                        <span className="vm-mobile-zone-cap">cap {z.capacity}</span>
                                    )}
                                    {tablesInZone.length > 0 && (
                                        <span className="vm-mobile-zone-tables">{tablesInZone.length} tables</span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SharedFloorPlanView;
